import {
  GoogleAuthProvider,
  fetchSignInMethodsForEmail,
  getAdditionalUserInfo,
  getRedirectResult,
  linkWithPopup,
  linkWithRedirect,
  signInWithPopup,
  signInWithRedirect,
  unlink,
} from "firebase/auth";
import { auth } from "../firebase";
import { logAuthAuditEvent, trackAuthAnalyticsEvent } from "./authTelemetry";

export const AUTH_PROVIDER_IDS = {
  PASSWORD: "password",
  GOOGLE: "google.com",
};

export const AUTH_PROVIDER_CONFIGS = [
  {
    providerId: AUTH_PROVIDER_IDS.PASSWORD,
    label: "Email & Password",
    iconClass: "fa fa-envelope",
  },
  {
    providerId: AUTH_PROVIDER_IDS.GOOGLE,
    label: "Google",
    iconClass: "fa fa-google",
  },
];

export const EXISTING_PASSWORD_ACCOUNT_MESSAGE =
  "An account already exists with this email. Please sign in with your password to connect Google login.";

export const GOOGLE_LINK_SUCCESS_MESSAGE =
  "Google login successfully connected.\nYou can now sign in using either Google or your password.";

export const GOOGLE_LOGIN_NO_ACCOUNT_MESSAGE =
  "No Lesson Launchpad account found for this Google login. Please create an account on the sign-up page, or sign in with email and password.";

export const GOOGLE_LOGIN_NO_ACCOUNT_ERROR_CODE = "llp/google-login-no-account";

const GOOGLE_REDIRECT_INTENT_KEY = "llp_google_auth_redirect_intent";
const PENDING_GOOGLE_LINK_EMAIL_KEY = "llp_pending_google_link_email";

export class GoogleLoginNoAccountError extends Error {
  constructor(message = GOOGLE_LOGIN_NO_ACCOUNT_MESSAGE) {
    super(message);
    this.name = "GoogleLoginNoAccountError";
    this.code = GOOGLE_LOGIN_NO_ACCOUNT_ERROR_CODE;
  }
}

const POPUP_FALLBACK_ERROR_CODES = new Set([
  "auth/popup-blocked",
  "auth/popup-closed-by-user",
  "auth/cancelled-popup-request",
  "auth/operation-not-supported-in-this-environment",
]);

export function normalizeAuthEmail(email) {
  return typeof email === "string" ? email.trim().toLowerCase() : "";
}

export function createGoogleProvider() {
  const provider = new GoogleAuthProvider();
  provider.setCustomParameters({ prompt: "select_account" });
  return provider;
}

export function getLinkedProviderIds(firebaseUser = auth.currentUser) {
  return (firebaseUser?.providerData || [])
    .map((provider) => provider.providerId)
    .filter(Boolean);
}

export function isAuthProviderLinked(providerId, firebaseUser = auth.currentUser) {
  return getLinkedProviderIds(firebaseUser).includes(providerId);
}

export function getProviderEmail(providerId, firebaseUser = auth.currentUser) {
  return firebaseUser?.providerData?.find((provider) => provider.providerId === providerId)?.email || "";
}

export function getAuthProviderRows(firebaseUser = auth.currentUser) {
  const linkedProviderIds = getLinkedProviderIds(firebaseUser);

  return AUTH_PROVIDER_CONFIGS.map((providerConfig) => ({
    ...providerConfig,
    connected: linkedProviderIds.includes(providerConfig.providerId),
    email: getProviderEmail(providerConfig.providerId, firebaseUser),
  }));
}

export function setPendingGoogleLinkEmail(email) {
  const normalizedEmail = normalizeAuthEmail(email);
  if (!normalizedEmail || typeof sessionStorage === "undefined") return;

  sessionStorage.setItem(PENDING_GOOGLE_LINK_EMAIL_KEY, normalizedEmail);
}

export function clearPendingGoogleLinkEmail() {
  if (typeof sessionStorage === "undefined") return;

  sessionStorage.removeItem(PENDING_GOOGLE_LINK_EMAIL_KEY);
}

export function shouldLinkPendingGoogleForEmail(email) {
  if (typeof sessionStorage === "undefined") return false;

  const pendingEmail = normalizeAuthEmail(sessionStorage.getItem(PENDING_GOOGLE_LINK_EMAIL_KEY));
  return Boolean(pendingEmail) && pendingEmail === normalizeAuthEmail(email);
}

export async function getSignInMethodsForNormalizedEmail(email) {
  const normalizedEmail = normalizeAuthEmail(email);
  if (!normalizedEmail) return [];

  return fetchSignInMethodsForEmail(auth, normalizedEmail);
}

export async function hasUnlinkedPasswordAccountForEmail(email) {
  const methods = await getSignInMethodsForNormalizedEmail(email);
  return methods.includes(AUTH_PROVIDER_IDS.PASSWORD) && !methods.includes(AUTH_PROVIDER_IDS.GOOGLE);
}

export function isDuplicateAccountError(error) {
  return [
    "auth/account-exists-with-different-credential",
    "auth/email-already-in-use",
    "auth/credential-already-in-use",
  ].includes(error?.code);
}

export function getDuplicateAccountEmail(error) {
  return normalizeAuthEmail(error?.customData?.email || error?.email);
}

function shouldFallbackToRedirect(error) {
  return POPUP_FALLBACK_ERROR_CODES.has(error?.code);
}

function setGoogleRedirectIntent(intent) {
  if (typeof sessionStorage === "undefined") return;

  sessionStorage.setItem(GOOGLE_REDIRECT_INTENT_KEY, intent);
}

export function consumeGoogleRedirectIntent() {
  if (typeof sessionStorage === "undefined") return "";

  const intent = sessionStorage.getItem(GOOGLE_REDIRECT_INTENT_KEY) || "";
  sessionStorage.removeItem(GOOGLE_REDIRECT_INTENT_KEY);
  return intent;
}

export async function signInWithGooglePopupOrRedirect(intent = "login") {
  const provider = createGoogleProvider();

  try {
    const result = await signInWithPopup(auth, provider);
    return { result, redirected: false };
  } catch (error) {
    if (!shouldFallbackToRedirect(error)) {
      throw error;
    }

    setGoogleRedirectIntent(intent);
    await signInWithRedirect(auth, provider);
    return { result: null, redirected: true };
  }
}

export function isGoogleSignInNewUser(result) {
  return getAdditionalUserInfo(result)?.isNewUser === true;
}

/**
 * Login must never create accounts. Firebase Google sign-in auto-creates Auth
 * users when none exist, so reject that case, delete the new Auth user, and
 * leave Register as the only Google signup path.
 */
export async function rejectNewGoogleUserOnLogin(result, source = "login") {
  if (!result?.user || !isGoogleSignInNewUser(result)) {
    return false;
  }

  const email = normalizeAuthEmail(result.user.email);
  const uid = result.user.uid;

  trackAuthAnalyticsEvent("login_google_rejected_new_user", {
    providerId: AUTH_PROVIDER_IDS.GOOGLE,
    source,
  });

  // Best-effort audit while still signed in; Auth user is deleted next.
  await logAuthAuditEvent("google_login_rejected_new_user", {
    providerId: AUTH_PROVIDER_IDS.GOOGLE,
    source,
    reason: "is_new_user",
    email,
    uid,
  });

  // Firestore usersNew/{uid} cannot be deleted from the client (rules forbid it).
  // cleanupUsersNewOnAuthDelete + ensureUsersNewOnAuthCreate's auth-still-exists
  // check must remove the shell the Auth onCreate backstop may have written.
  try {
    await result.user.delete();
  } catch (deleteError) {
    try {
      await auth.signOut();
    } catch (signOutError) {
      // Prefer surfacing the original delete failure.
      console.warn("Failed to sign out after rejecting Google login signup:", signOutError);
    }
    throw deleteError;
  }

  throw new GoogleLoginNoAccountError();
}

export async function linkGooglePopupOrRedirect(intent = "link") {
  if (!auth.currentUser) {
    throw new Error("You must be signed in before connecting Google.");
  }

  const provider = createGoogleProvider();

  try {
    const result = await linkWithPopup(auth.currentUser, provider);
    return { result, redirected: false };
  } catch (error) {
    if (!shouldFallbackToRedirect(error)) {
      throw error;
    }

    setGoogleRedirectIntent(intent);
    await linkWithRedirect(auth.currentUser, provider);
    return { result: null, redirected: true };
  }
}

export async function getGoogleRedirectResult() {
  const result = await getRedirectResult(auth);
  return {
    result,
    intent: result ? consumeGoogleRedirectIntent() : "",
  };
}

export async function linkPendingGoogleAfterPasswordSignIn(source = "login") {
  const result = await linkGooglePopupOrRedirect("link");
  if (result.redirected) {
    return result;
  }

  clearPendingGoogleLinkEmail();
  trackAuthAnalyticsEvent("provider_link_success", {
    providerId: AUTH_PROVIDER_IDS.GOOGLE,
    source,
  });
  await logAuthAuditEvent("auth_provider_linked", {
    providerId: AUTH_PROVIDER_IDS.GOOGLE,
    source,
  });
  return result;
}

export async function unlinkAuthProvider(providerId, source = "settings") {
  if (!auth.currentUser) {
    throw new Error("You must be signed in before removing a sign-in method.");
  }

  const linkedProviderIds = getLinkedProviderIds(auth.currentUser);
  if (linkedProviderIds.length <= 1) {
    throw new Error("You cannot remove your only authentication method.");
  }

  const result = await unlink(auth.currentUser, providerId);
  await auth.currentUser.reload();
  await logAuthAuditEvent("auth_provider_unlinked", {
    providerId,
    source,
  });
  return result;
}

export async function logDuplicateAccountAttempt({ email, source, reason } = {}) {
  setPendingGoogleLinkEmail(email);
  await logAuthAuditEvent("duplicate_account_attempt", {
    providerId: AUTH_PROVIDER_IDS.GOOGLE,
    source,
    reason,
  });
}

export function getGoogleAuthErrorMessage(error) {
  if (isDuplicateAccountError(error)) {
    return EXISTING_PASSWORD_ACCOUNT_MESSAGE;
  }

  if (
    error instanceof GoogleLoginNoAccountError
    || error?.code === GOOGLE_LOGIN_NO_ACCOUNT_ERROR_CODE
  ) {
    return GOOGLE_LOGIN_NO_ACCOUNT_MESSAGE;
  }

  switch (error?.code) {
    case "auth/popup-closed-by-user":
    case "auth/cancelled-popup-request":
      return "Google sign-in was cancelled. Please try again when you are ready.";
    case "auth/popup-blocked":
      return "Your browser blocked the Google sign-in popup. Please allow popups for Lesson Launchpad and try again.";
    case "auth/network-request-failed":
      return "Network error. Check your connection and try again.";
    case "auth/requires-recent-login":
      return "Please sign out and sign back in before changing your authentication methods.";
    case "auth/provider-already-linked":
      return "Google is already connected to this account.";
    case "auth/credential-already-in-use":
      return "This Google account is already connected to another Lesson Launchpad account.";
    default:
      return "Unable to complete Google sign-in right now. Please try again.";
  }
}
